import { useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from "react";
import { CheckCircle2, Loader2, Plus, Save, Trash2, X } from "lucide-react";
import type {
  StudentExecutionFelt,
  StudentExecutionItem,
  StudentWorkoutItem,
  UpdateExecutionItemPayload,
} from "@/lib/types";
import { ExerciseThumb } from "./ExerciseThumb";
import { Prescription } from "./Prescription";
import { guessSetsFromDetail, parseWorkoutDetail } from "./workoutDetail";

interface ItemExecutionModalProps {
  open: boolean;
  onClose: () => void;
  workoutItem: StudentWorkoutItem;
  /** Registro existente da execução (retomada). null = primeira vez. */
  existing: StudentExecutionItem | null;
  onSave: (
    workoutItemId: string,
    payload: UpdateExecutionItemPayload,
  ) => Promise<{ ok: boolean; error?: string }>;
}

function NumberInput({
  value,
  onChange,
  min,
  max,
  step,
  placeholder,
  ariaLabel,
  large,
}: {
  value: string;
  onChange: (v: string) => void;
  min?: number;
  max?: number;
  step?: string;
  placeholder?: string;
  ariaLabel?: string;
  large?: boolean;
}) {
  return (
    <input
      type="number"
      inputMode="decimal"
      value={value}
      onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      min={min}
      max={max}
      step={step}
      placeholder={placeholder}
      aria-label={ariaLabel}
      className={[
        "w-full rounded-lg border border-border-subtle bg-canvas/70 text-ink placeholder:text-faint",
        "focus:border-accent-soft focus:outline-none focus:ring-1 focus:ring-accent-soft",
        large ? "px-3 py-3 text-center text-2xl font-semibold tabular-nums" : "px-2.5 py-2 text-sm",
      ].join(" ")}
    />
  );
}

const FELT_OPTIONS: Array<{
  value: StudentExecutionFelt;
  emoji: string;
  label: string;
  description: string;
}> = [
  { value: "easy", emoji: "😅", label: "Tranquilo", description: "Pegou leve" },
  { value: "medium", emoji: "💪", label: "OK", description: "Na medida" },
  { value: "hard", emoji: "🥵", label: "Difícil", description: "Foi puxado" },
];

export function ItemExecutionModal({
  open,
  onClose,
  workoutItem,
  existing,
  onSave,
}: ItemExecutionModalProps) {
  const guessedSets = useMemo(() => guessSetsFromDetail(workoutItem.detail), [workoutItem.detail]);
  const parsedDetail = useMemo(() => parseWorkoutDetail(workoutItem.detail), [workoutItem.detail]);

  const [setsDone, setSetsDone] = useState<string>(() =>
    existing?.sets_done != null ? String(existing.sets_done) : "",
  );
  const [reps, setReps] = useState<string[]>(() => {
    if (existing && existing.reps_per_set?.length) return existing.reps_per_set.map((n) => String(n));
    return Array.from({ length: guessedSets }, () => "");
  });
  const [loadKg, setLoadKg] = useState<string>(() =>
    existing?.load_kg != null ? String(existing.load_kg) : "",
  );
  const [rpe, setRpe] = useState<number | null>(() => (existing?.rpe != null ? existing.rpe : null));
  const [felt, setFelt] = useState<StudentExecutionFelt | null>(() => existing?.felt ?? null);
  const [notes, setNotes] = useState<string>(() => existing?.notes ?? "");

  const [submitting, setSubmitting] = useState<"complete" | "save" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const dialogRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, submitting, onClose]);

  useEffect(() => {
    if (!open || !dialogRef.current) return;
    const first = dialogRef.current.querySelector<HTMLElement>(
      "input, textarea, button[data-autofocus]",
    );
    first?.focus();
  }, [open]);

  if (!open) return null;

  function setRepAt(idx: number, v: string) {
    setReps((prev) => prev.map((r, i) => (i === idx ? v : r)));
  }
  function addRepField() {
    setReps((prev) => [...prev, ""]);
  }
  function removeRepField(idx: number) {
    setReps((prev) => prev.filter((_, i) => i !== idx));
  }

  function buildPayload(completed: boolean): UpdateExecutionItemPayload {
    const setsParsed = setsDone.trim() === "" ? null : parseInt(setsDone, 10);
    const loadParsed = loadKg.trim() === "" ? null : parseFloat(loadKg);
    const repsParsed = reps
      .map((r) => (r.trim() === "" ? null : parseInt(r, 10)))
      .filter((n): n is number => n !== null && Number.isFinite(n) && n > 0);

    return {
      sets_done: setsParsed != null && Number.isFinite(setsParsed) ? setsParsed : null,
      reps_per_set: repsParsed,
      load_kg: loadParsed != null && Number.isFinite(loadParsed) ? loadParsed : null,
      rpe,
      felt,
      notes: notes.trim() === "" ? null : notes.trim(),
      completed,
    };
  }

  async function submit(completed: boolean) {
    if (submitting) return;
    setSubmitting(completed ? "complete" : "save");
    setError(null);
    const res = await onSave(workoutItem.id, buildPayload(completed));
    if (res.ok) {
      onClose();
    } else {
      setError(res.error ?? "Não consegui salvar agora.");
      setSubmitting(null);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="item-modal-title"
      className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="animate-fade-in relative max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-border-subtle bg-surface/95 backdrop-blur-xl sm:rounded-2xl"
      >
        <header className="sticky top-0 z-10 flex items-start gap-3 border-b border-border-subtle bg-surface/95 px-4 py-3 backdrop-blur-md sm:px-5 sm:py-4">
          <div className="shrink-0">
            <ExerciseThumb item={workoutItem} size="small" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="item-modal-title" className="font-display text-base text-ink sm:text-lg">
              {workoutItem.line}
            </h2>
            <Prescription parsed={parsedDetail} />
          </div>
          <button
            type="button"
            onClick={() => !submitting && onClose()}
            disabled={!!submitting}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-faint transition-colors hover:bg-surface-hover hover:text-ink disabled:opacity-50"
            aria-label="Fechar"
          >
            <X size={16} aria-hidden />
          </button>
        </header>

        <div className="space-y-5 px-4 py-4 sm:px-5 sm:py-5">
          <Section title="Séries feitas" description="Quantas você completou no total">
            <NumberInput
              value={setsDone}
              onChange={setSetsDone}
              min={0}
              max={20}
              step="1"
              placeholder="0"
              ariaLabel="Séries feitas"
              large
            />
          </Section>

          <Section
            title="Reps por série"
            description="Quantas repetições em cada série"
            action={
              <button
                type="button"
                onClick={addRepField}
                className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-elevated px-2 py-0.5 text-[11px] font-medium text-muted transition-colors hover:border-border-strong hover:text-ink"
                aria-label="Adicionar série"
              >
                <Plus size={11} aria-hidden />
                Adicionar
              </button>
            }
          >
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {reps.map((r, idx) => (
                <div
                  key={`rep-${idx}`}
                  className="relative flex items-center gap-1 rounded-lg border border-border-subtle bg-canvas/40 px-1.5 py-1"
                >
                  <span className="text-[10px] font-medium uppercase tracking-wider text-faint">
                    {idx + 1}ª
                  </span>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={r}
                    onChange={(e) => setRepAt(idx, e.target.value)}
                    min={0}
                    max={999}
                    step="1"
                    placeholder="--"
                    aria-label={`Reps série ${idx + 1}`}
                    className="w-full bg-transparent text-sm font-medium text-ink placeholder:text-faint focus:outline-none"
                  />
                  {reps.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRepField(idx)}
                      className="rounded p-0.5 text-faint transition-colors hover:text-danger"
                      aria-label={`Remover série ${idx + 1}`}
                    >
                      <Trash2 size={11} aria-hidden />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </Section>

          <Section title="Carga (kg)" description="Peso usado">
            <NumberInput
              value={loadKg}
              onChange={setLoadKg}
              min={0}
              step="0.5"
              placeholder="0"
              ariaLabel="Carga em quilos"
              large
            />
          </Section>

          <Section title="RPE (esforço percebido)" description="0 = sem esforço · 10 = falha total">
            <div className="grid grid-cols-11 gap-1">
              {Array.from({ length: 11 }, (_, n) => (
                <button
                  key={`rpe-${n}`}
                  type="button"
                  onClick={() => setRpe(rpe === n ? null : n)}
                  className={[
                    "h-9 rounded-lg border text-xs font-semibold transition-all duration-150",
                    rpe === n
                      ? "scale-105 border-accent-soft bg-accent text-ink shadow-sm"
                      : "border-border-subtle bg-canvas/50 text-muted hover:border-border-strong hover:text-ink",
                  ].join(" ")}
                  aria-label={`RPE ${n}`}
                  aria-pressed={rpe === n}
                >
                  {n}
                </button>
              ))}
            </div>
          </Section>

          <fieldset>
            <legend className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted">
              Como foi?
            </legend>
            <p className="mb-2 text-[11px] text-faint">Sua sensação geral</p>
            <div className="grid grid-cols-3 gap-2">
              {FELT_OPTIONS.map((opt) => {
                const selected = felt === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFelt(felt === opt.value ? null : opt.value)}
                    className={[
                      "flex flex-col items-center gap-1 rounded-xl border px-2 py-3 text-center transition-all duration-150",
                      selected
                        ? "border-accent-soft bg-accent/15 text-ink shadow-sm"
                        : "border-border-subtle bg-canvas/40 text-muted hover:border-border-strong hover:bg-canvas/60 hover:text-ink",
                    ].join(" ")}
                    aria-pressed={selected}
                    aria-label={`${opt.label}: ${opt.description}`}
                  >
                    <span className="text-2xl leading-none" aria-hidden>
                      {opt.emoji}
                    </span>
                    <span className={["text-sm font-medium", selected ? "text-ink" : ""].join(" ")}>
                      {opt.label}
                    </span>
                    <span className="text-[10px] text-faint">{opt.description}</span>
                  </button>
                );
              })}
            </div>
          </fieldset>

          <Section title="Comentário" description="Algo importante pro seu treinador?">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="ex: ombro doeu na 3ª série; usei pegada mais aberta"
              className="w-full resize-none rounded-lg border border-border-subtle bg-canvas/70 px-3 py-2 text-sm text-ink placeholder:text-faint focus:border-accent-soft focus:outline-none focus:ring-1 focus:ring-accent-soft"
            />
          </Section>

          {error && (
            <div
              role="alert"
              className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
            >
              {error}
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 z-10 flex flex-col-reverse gap-2 border-t border-border-subtle bg-surface/95 px-4 py-3 backdrop-blur-md sm:flex-row sm:items-center sm:justify-end sm:gap-2 sm:px-5 sm:py-4">
          <button
            type="button"
            onClick={() => submit(false)}
            disabled={!!submitting}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-border-subtle bg-elevated px-3 py-2.5 text-sm font-medium text-ink transition-colors hover:border-border-strong hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting === "save" ? (
              <Loader2 size={14} className="animate-spin" aria-hidden />
            ) : (
              <Save size={14} aria-hidden />
            )}
            Salvar e continuar
          </button>
          <button
            type="button"
            data-autofocus
            onClick={() => submit(true)}
            disabled={!!submitting}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-accent px-4 py-2.5 text-sm font-medium text-ink shadow-sm transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting === "complete" ? (
              <Loader2 size={14} className="animate-spin" aria-hidden />
            ) : (
              <CheckCircle2 size={14} aria-hidden />
            )}
            Marcar como feito
          </button>
        </footer>
      </div>
    </div>
  );
}

function Section({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="mb-2 flex items-end justify-between gap-2">
        <div>
          <p className="block text-xs font-medium uppercase tracking-wider text-muted">{title}</p>
          {description && <p className="mt-0.5 text-[11px] text-faint">{description}</p>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}
